import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import * as IBAN from 'iban';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '~environments/environment';
import {Bank, SepaMandate} from '../../models';

/**
 * Service for creating and updating bank entity.
 */
@Injectable({
  providedIn: 'root'
})
export class BankService {
  public API_URL: any = environment.envAPI;

  public bankList: BehaviorSubject<Array<Bank>> = new BehaviorSubject<Array<Bank>>(null);
  public castBankList: any = this.bankList.asObservable();

  public sepaMandateList: BehaviorSubject<Array<SepaMandate>> = new BehaviorSubject<Array<SepaMandate>>(null);
  public castSepaMandateList: any = this.sepaMandateList.asObservable();

  public updateBank: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castUpdateBank: any = this.updateBank.asObservable();

  constructor(private http: HttpClient) {
  }

  /**
   * Get bank data.
   */
  public get(customerId: number): Observable<Array<Bank>> {
    return this.http.get<Array<Bank>>(`${this.API_URL}/customers/${customerId}/bank-accounts`)
      .pipe(
        map((response: Array<any>) => response['hydra:member'].map((bankData: Bank) => new Bank(bankData))
        )
      );
  }

  /**
   * Create a new bank data.
   */
  public create(bank: Bank): Observable<Bank> {
    return this.http.post<Bank>(`${this.API_URL}/bank-accounts`, bank);
  }

  /**
   * Update bank data.
   */
  public update(bank: Bank): Observable<Bank> {
    return this.http.patch<Bank>(`${this.API_URL}/bank-accounts/${bank.id}`, bank);
  }

  /**
   * Delete bank data.
   */
  public delete(bankId: number): any {
    return this.http.delete(`${this.API_URL}/bank-accounts/${bankId}`);
  }

  /**
   * Get bank account SEPA mandates.
   */
  public getSepaMandates(bankId: number): Observable<Array<SepaMandate>> {
    return this.http.get<Array<SepaMandate>>(`${this.API_URL}/bank-accounts/${bankId}/sepa-mandates`)
      .pipe(
        map((response: Array<any>) => response['hydra:member'].map((sepaMandates: SepaMandate) => new SepaMandate(sepaMandates))
        )
      );
  }

  /**
   * Verify the iban provided and get returned the full bank details
   */
  public getVerifiedIban(iban: any): any {
    return this.http.post<Bank>(`${this.API_URL}/bank-accounts/verify`, iban);
  }

  /**
   * Adds values to the observer.
   */
  public announceUpdatedBankData(bank: Bank): void {
    this.updateBank.next(bank);
  }

  /**
   * Clear the address values from the observable.
   */
  public clearBankDataUpdate(): void {
    this.updateBank.next(null);
  }

  /**
   * Set a new sepa mandates list
   */
  public setSepaMandateList(sepaMandateList: Array<SepaMandate>): void {
    this.sepaMandateList.next(sepaMandateList);
  }

  /**
   * Set a new bank list
   */
  public setBankList(bankList: Array<Bank>): void {
    bankList.forEach(el => el.IBAN = IBAN.printFormat(el.IBAN));
    this.bankList.next(bankList);
  }

  /**
   * Delete a bank element from the bank list using the id
   */
  public deleteBankFromList(bankId: number): void {
    const index = this.bankList.getValue().findIndex(el => el.id === bankId);
    this.bankList.getValue().splice(index, 1);
  }

  /**
   * Add a new entry to the bank list
   */
  public addToBankList(bank: Bank): void {
    bank.IBAN = IBAN.printFormat(bank.IBAN);
    this.bankList.getValue().push(bank);
  }

  /**
   * Update a certain entry in the bank list
   */
  public updateBankData(bank: Bank): void {
    bank.IBAN = IBAN.printFormat(bank.IBAN);
    const index = this.bankList.getValue().findIndex(el => el.id === bank.id);
    this.bankList.getValue()[index] = bank;
  }
}
